import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFocusEffect } from '@react-navigation/native';

import { discardCrossProductImport } from '@/feature/entry-share/hooks/use-linking';
import { useProductTransitionStore } from '@/feature/product-transition/use-product-transition-store';
import { renderPredictionTerms } from '@/feature/terms-and-conditions/components/PredictionTermsContent';
import { useTermsAndConditionsModal } from '@/feature/terms-and-conditions/components/TermsAndConditionModalProvider';
import { useHasUserAcceptedProductTerms } from '@/feature/terms-and-conditions/hooks/use-has-user-accepted-product-terms';
import {
    polymarketKycPendingSelector,
    polymarketKycVerifiedSelector,
    useAuthUserConfig,
} from '@/hooks/use-auth-user-config';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { Product } from '@/hooks/use-jurisdiction';
import { runAfterInteractions } from '@/utils/runAfterInteractions';

import { usePredictionKycSubmit } from './use-prediction-kyc-submit';

const PENDING_REVIEW_POLL_INTERVAL_MS = 5000;
const PENDING_REVIEW_POLL_TIMEOUT_MS = 30000;

export type PredictionGateStatus =
    | 'checkingConfig' // waiting on the first auth-user config load
    | 'inactive' // backend KYC flags not present yet -> render the vertical un-gated
    | 'needsTnc' // acknowledgement required -> the modal is presented
    | 'submittingKyc' // running / awaiting the Polymarket KYC check
    | 'eligible' // verified -> render the vertical
    | 'ineligible' // blocked / failed / declined-with-no-fallback -> "unavailable"
    | 'pending' // submitted, awaiting the exchange partner's review -> "pending review"
    | 'inReview'; // REVIEW (docv) -> "unavailable" for now (DocV capture will be handled in CSP-2178)

/**
 * Access gate for the Predictions vertical.
 *
 * requires a T&C acknowledgement, then a passing KYC check, before the vertical is shown.
 */
export const usePredictionAccessGate = (): { status: PredictionGateStatus } => {
    const { availableProducts } = useAvailableProducts();
    const kyc = usePredictionKycSubmit();

    // Perform polling every 5s up to 30s (config dependent) to check when Polymarket's
    // KYC review is complete.
    const pendingPollStartedAtRef = useRef<number | null>(null);
    const configQuery = useAuthUserConfig({
        refetchInterval: query => {
            const data = query.state.data;
            const shouldPoll =
                !!data &&
                polymarketKycVerifiedSelector(data) !== true &&
                (polymarketKycPendingSelector(data) === true || kyc.isSuccess);
            if (!shouldPoll) {
                pendingPollStartedAtRef.current = null;
                return false;
            }
            pendingPollStartedAtRef.current ??= Date.now();
            const timedOut = Date.now() - pendingPollStartedAtRef.current >= PENDING_REVIEW_POLL_TIMEOUT_MS;
            return timedOut ? false : PENDING_REVIEW_POLL_INTERVAL_MS;
        },
    });
    const config = configQuery.data?.data.config;
    const verified = config?.POLYMARKET_KYC_VERIFIED;
    const eligible = config?.POLYMARKET_KYC_ELIGIBLE;
    const pending = config?.POLYMARKET_KYC_PENDING;

    const { isAccepted: configTncAccepted } = useHasUserAcceptedProductTerms();

    // Bridge acceptance locally so the gate advances the moment the user accepts, without waiting
    const [acceptedLocally, setAcceptedLocally] = useState(false);
    const tncAccepted = configTncAccepted || acceptedLocally;

    const [forcedIneligible, setForcedIneligible] = useState(false);

    const status: PredictionGateStatus = useMemo(() => {
        if (configQuery.isLoading && !configQuery.data) {
            return 'checkingConfig';
        }
        if (verified === undefined && eligible === undefined) {
            return 'inactive';
        }
        if (verified === true) {
            return 'eligible';
        }
        if (pending === true) {
            return 'pending';
        }
        if (forcedIneligible) {
            return 'ineligible';
        }
        if (!tncAccepted) {
            return 'needsTnc';
        }
        if (eligible !== true) {
            return 'ineligible';
        }
        if (kyc.isError) {
            return 'ineligible';
        }
        if (kyc.isSuccess) {
            const result = kyc.data;
            // `success` only means the call went through — the outcome is in `decision`.
            if (!result?.success) {
                return 'ineligible';
            }
            if (result.docv || result.decision === 'REVIEW') {
                return 'inReview';
            }
            if (result.decision === 'REJECT') {
                return 'ineligible';
            }
            // Approved: do NOT enter the product off the KYC response — an accepted submission
            // can still land as PENDING (accepted but not yet provisioned). Stay on the loader
            // until the config re-fetch confirms the final state via the cascade above
            // (VERIFIED -> vertical, PENDING -> pending screen).
        }
        return 'submittingKyc';
    }, [
        configQuery.isLoading,
        configQuery.data,
        verified,
        eligible,
        pending,
        forcedIneligible,
        tncAccepted,
        kyc.isError,
        kyc.isSuccess,
        kyc.data,
    ]);

    // Fire the KYC check exactly once per entry. `isIdle` blocks re-fires on re-render/refocus;
    // the synchronous ref closes the React StrictMode double-invoke race. Never a query (the POST
    // is not idempotent) and never a mutation with `onSuccess` (the result is not the final state).

    const kycFiredRef = useRef(false);
    useEffect(() => {
        const needsKyc = verified === false && eligible === true && tncAccepted;
        if (needsKyc && kyc.isIdle && !kycFiredRef.current) {
            kycFiredRef.current = true;
            kyc.mutate();
        }
    }, [verified, eligible, tncAccepted, kyc]);

    const declineTnc = useCallback(() => {
        const fallback = availableProducts.find(p => p !== Product.Prediction);
        if (!fallback) {
            setForcedIneligible(true);
            return;
        }
        // the user chose to leave; the gate's terminal statuses handle the no-fallback path
        discardCrossProductImport(Product.Prediction);
        useProductTransitionStore.getState().actions.triggerTransition(fallback);
    }, [availableProducts]);

    const { showTermsAndConditionsModal } = useTermsAndConditionsModal();
    const presentedRef = useRef(false);
    useFocusEffect(
        useCallback(() => {
            if (status !== 'needsTnc' || presentedRef.current) {
                return;
            }
            presentedRef.current = true;
            runAfterInteractions(() => {
                showTermsAndConditionsModal({
                    renderContent: renderPredictionTerms,
                    onSuccess: () => setAcceptedLocally(true),
                    onCancel: declineTnc,
                    onTermsError: () => setForcedIneligible(true),
                });
            });
        }, [status, showTermsAndConditionsModal, declineTnc])
    );

    return { status };
};
